<template>
    <Card>
        <CardHeader class="flex flex-row items-center justify-between space-y-0">
            <div class="flex items-center space-x-2">
                <CardTitle class="text-sm font-medium">{{ title }}</CardTitle>
                <Info v-if="infoIcon && !loading" :class="titleIconClass" @click="errors?.length && onShowError()" />
            </div>
            <component :is="icon" class="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
            <SkeletonLoader preset="stat-card" :loading="loading" />
            <div v-if="!loading" class="space-y-3">
                <!-- Main value area - flexible layout for simple vs complex HTML -->
                <div :class="isComplexHtml ? 'space-y-2' : 'flex items-center'">
                    <div :class="isComplexHtml ? '' : 'text-2xl font-bold'" v-html="purify(value)"></div>

                    <!-- Info badges - only show if we have simple values and infoItems -->
                    <div v-if="infoItems.length && !isComplexHtml" class="flex flex-wrap gap-1 ml-3">
                        <template v-for="(info, idx) in infoItems" :key="idx">
                            <Badge v-if="info.colorClass" variant="outline" class="text-xs" :class="info.colorClass">
                                {{ info.value }} {{ info.label }}
                            </Badge>
                            <span v-else class="text-xs text-muted-foreground">{{ info.value }} {{ info.label }}</span>
                        </template>
                    </div>

                    <!-- Tooltip icon - only for simple values -->
                    <TooltipProvider v-if="valueTooltip && !infoItems.length && !isComplexHtml">
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Info :class="valueIconClass" />
                            </TooltipTrigger>
                            <TooltipContent side="bottom" class="max-w-xs">
                                <div v-html="purify(valueTooltip)"></div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>

                <!-- Info badges for complex HTML values - show underneath -->
                <div v-if="infoItems.length && isComplexHtml" class="flex flex-wrap gap-1">
                    <template v-for="(info, idx) in infoItems" :key="idx">
                        <Badge v-if="info.colorClass" variant="outline" class="text-xs" :class="info.colorClass">
                            {{ info.value }} {{ info.label }}
                        </Badge>
                        <span v-else class="text-xs text-muted-foreground">{{ info.value }} {{ info.label }}</span>
                    </template>
                </div>

                <!-- Tooltip for complex HTML values -->
                <TooltipProvider v-if="valueTooltip && isComplexHtml">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <div class="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
                                <Info class="h-3 w-3" />
                                <span>More details</span>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" class="max-w-xs">
                            <div v-html="purify(valueTooltip)"></div>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </CardContent>
    </Card>
</template>

<script setup>
import { Info } from 'lucide-vue-next';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { Badge } from '@/components/ui/badge';
import { useErrorDialog } from '@/composables/useDialog';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import { computed } from 'vue';

const props = defineProps({
    title: String,
    value: [String, Number, HTMLElement],
    icon: [Object, String, Function],
    infoIcon: Boolean,
    infoItems: { type: Array, default: () => [] },
    errors: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    valueTooltip: { type: String, default: '' }
});

const onShowError = () => {
    useErrorDialog({ errors: props.errors, width: 65 });
};

const purify = (html) => (!_.isNaN(Number(html)) ? html : DOMPurify.sanitize(html));

// Dynamic icon sizing based on context
const titleIconClass = computed(() => {
    const baseClasses = 'text-muted-foreground cursor-pointer';
    // For broken status, make the icon red and slightly larger
    if (props.title === 'Status' && props.value?.includes?.('Broken')) {
        return `size-4 text-destructive ${baseClasses}`;
    }
    // Smaller icon for title area
    return `size-4 ${baseClasses}`;
});

const valueIconClass = computed(() => {
    const baseClasses = 'text-muted-foreground cursor-pointer ml-2';
    // Check if value appears to be a large number (XL size)
    const valueStr = String(props.value || '');
    const isLargeValue = valueStr.length > 6 || valueStr.includes('xl') || valueStr.includes('XL');

    // Larger icon for large values, smaller for normal values
    return isLargeValue ? `h-5 w-5 ${baseClasses}` : `h-4 w-4 ${baseClasses}`;
});

// Detect complex HTML values that need different layout
const isComplexHtml = computed(() => {
    const valueStr = String(props.value || '');
    // Check for complex HTML structures (divs, spans with classes, etc.)
    return valueStr.includes('<div') || valueStr.includes('class=') || valueStr.includes('space-y');
});
</script>

<style scoped></style>
